<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useQuery } from '@tanstack/vue-query'
import { useI18n } from 'vue-i18n'
import { Roles } from '@/constants.ts'
import invitationsHttpService, {
  type ResultsRecipient,
} from '@/services/http/invitationsHttpService.ts'
import PhoneContact from '@/pages/registrations/PhoneContact.vue'

definePage({
  meta: {
    allowedRoles: [Roles.Admin, Roles.Manager],
  },
})

const { t, locale } = useI18n()
const route = useRoute()

// params типизированы как объединение всех роутов; для динамического [id] берём id явно.
const batchId = computed(() => String((route.params as Record<string, string>).id))

const batchQuery = useQuery({
  queryKey: computed(() => ['admin', 'results-broadcast', batchId.value, locale.value]),
  queryFn: () => invitationsHttpService.getResultsBatch(batchId.value),
})

const batch = computed(() => batchQuery.data.value ?? null)
const funnel = computed(() => batch.value?.funnel ?? null)
const recipients = computed<ResultsRecipient[]>(() => batch.value?.recipients ?? [])

// Воронка результатов: recipients → sent → clicked → downloaded → shared. Цвета по логике этапа —
// как в детали приглашений: отправлено — голубой, клики — фиолетовый, скачивания — бирюзовый,
// шеринг — зелёный (финальная цель воронки).
const funnelBoxes = computed(() => {
  const f = funnel.value
  if (!f) return []
  return [
    {
      key: 'recipients',
      label: t('invitations.results.detail.funnel.recipients'),
      count: f.recipients,
      color: '',
    },
    {
      key: 'sent',
      label: t('invitations.results.detail.funnel.sent'),
      count: f.sent,
      color: 'text-sky-600 dark:text-sky-400',
    },
    {
      key: 'clicked',
      label: t('invitations.results.detail.funnel.clicked'),
      count: f.clicked,
      color: 'text-violet-600 dark:text-violet-400',
    },
    {
      key: 'downloaded',
      label: t('invitations.results.detail.funnel.downloaded'),
      count: f.downloaded,
      color: 'text-teal-600 dark:text-teal-400',
    },
    {
      key: 'shared',
      label: t('invitations.results.detail.funnel.shared'),
      count: f.shared,
      color: 'text-green-600 dark:text-green-400',
    },
  ]
})

function formatDateTime(value: string | null): string {
  if (value == null) return '—'
  const date = new Date(value)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}
</script>

<template>
  <section class="flex flex-col gap-4 w-full">
    <div class="flex flex-wrap items-center justify-between gap-2 w-full">
      <div class="flex flex-col gap-1">
        <RouterLink to="/invitations?tab=results" class="text-sm text-primary">
          ← {{ t('invitations.results.detail.back') }}
        </RouterLink>
        <h1 class="text-xl font-bold">{{ t('invitations.results.detail.title') }}</h1>
        <span v-if="batch" class="text-sm text-surface-500">
          {{ batch.eventName ?? '—' }} · {{ formatDateTime(batch.createdAt) }}
        </span>
      </div>
    </div>

    <!-- Воронка -->
    <Card v-if="funnel">
      <template #title>{{ t('invitations.results.detail.funnel.title') }}</template>
      <template #content>
        <div class="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <div
            v-for="box in funnelBoxes"
            :key="box.key"
            class="flex flex-col gap-1 rounded border border-surface-200 dark:border-surface-700 p-3"
            :data-test="`funnel-${box.key}`"
          >
            <span class="text-xs text-surface-500">{{ box.label }}</span>
            <span class="text-2xl font-bold" :class="box.color">{{ box.count }}</span>
          </div>
        </div>
      </template>
    </Card>

    <!-- Получатели -->
    <DataTable
      :value="recipients"
      :loading="batchQuery.isLoading.value"
      data-key="invitationId"
      class="w-full"
    >
      <template #empty>
        <div class="text-sm text-surface-500 py-4">
          {{ t('invitations.results.detail.recipients.empty') }}
        </div>
      </template>

      <Column :header="t('invitations.results.detail.recipients.phone')">
        <template #body="{ data }"><PhoneContact :phone="data.phone" /></template>
      </Column>
      <Column :header="t('invitations.results.detail.recipients.sendStatus')">
        <template #body="{ data }">{{ data.sendStatus ?? '—' }}</template>
      </Column>
      <Column :header="t('invitations.results.detail.recipients.deliveryStatus')">
        <template #body="{ data }">{{ data.deliveryStatus ?? '—' }}</template>
      </Column>
      <Column :header="t('invitations.results.detail.recipients.clickedAt')">
        <template #body="{ data }">{{ formatDateTime(data.clickedAt) }}</template>
      </Column>
      <Column :header="t('invitations.results.detail.recipients.downloadedAt')">
        <template #body="{ data }">{{ formatDateTime(data.downloadedAt) }}</template>
      </Column>
      <Column :header="t('invitations.results.detail.recipients.sharedAt')">
        <template #body="{ data }">{{ formatDateTime(data.sharedAt) }}</template>
      </Column>
    </DataTable>
  </section>
</template>
